import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { categories } from "@/mocks/manga";

interface NavbarProps {
  isDark: boolean;
  toggleTheme: () => void;
}

export default function Navbar({ isDark, toggleTheme }: NavbarProps) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setSearchOpen(false);
  }, [location]);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background-50/95 backdrop-blur-md shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center gap-8">
            <Link
              to="/"
              className={`font-heading text-2xl whitespace-nowrap transition-colors ${
                scrolled ? "text-foreground-950" : "text-white"
              }`}
            >
              漫蛙
            </Link>

            <div className="hidden md:flex items-center gap-6">
              <Link
                to="/"
                className={`text-sm whitespace-nowrap transition-colors ${
                  scrolled
                    ? "text-foreground-700 hover:text-foreground-950"
                    : "text-white/80 hover:text-white"
                }`}
              >
                首页
              </Link>
              {categories.slice(0, 4).map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`text-sm whitespace-nowrap transition-colors ${
                    scrolled
                      ? "text-foreground-700 hover:text-foreground-950"
                      : "text-white/80 hover:text-white"
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
              <Link
                to="/about"
                className={`text-sm whitespace-nowrap transition-colors ${
                  scrolled
                    ? "text-foreground-700 hover:text-foreground-950"
                    : "text-white/80 hover:text-white"
                }`}
              >
                关于我们
              </Link>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Search */}
            <div className="relative">
              <button
                onClick={() => setSearchOpen(!searchOpen)}
                className={`w-9 h-9 flex items-center justify-center rounded-full transition-colors cursor-pointer ${
                  scrolled
                    ? "text-foreground-600 hover:bg-background-200"
                    : "text-white/80 hover:text-white hover:bg-white/10"
                }`}
                aria-label="搜索"
              >
                <i className="ri-search-line text-lg"></i>
              </button>
              {searchOpen && (
                <div className="absolute right-0 top-full mt-2 w-72 bg-background-50 rounded-lg p-3 border border-background-200/70">
                  <div className="flex items-center gap-2 bg-background-100 rounded-md px-3 py-2">
                    <i className="ri-search-line text-foreground-400 text-sm"></i>
                    <input
                      type="text"
                      placeholder="搜索漫画、作者..."
                      className="bg-transparent text-sm text-foreground-950 placeholder-foreground-400 outline-none w-full"
                      autoFocus
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Theme toggle */}
            <button
              onClick={toggleTheme}
              className={`w-9 h-9 flex items-center justify-center rounded-full transition-colors cursor-pointer ${
                scrolled
                  ? "text-foreground-600 hover:bg-background-200"
                  : "text-white/80 hover:text-white hover:bg-white/10"
              }`}
              aria-label={isDark ? "切换日间模式" : "切换夜间模式"}
            >
              <i
                className={`text-lg ${
                  isDark ? "ri-sun-line" : "ri-moon-line"
                }`}
              ></i>
            </button>

            {/* Login */}
            <Link
              to="/"
              className={`hidden md:inline-flex px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                scrolled
                  ? "bg-primary-500 text-white hover:bg-primary-600"
                  : "bg-white/20 text-white border border-white/30 hover:bg-white/30"
              }`}
            >
              登录 / 注册
            </Link>

            {/* Mobile menu */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className={`md:hidden w-9 h-9 flex items-center justify-center rounded-full transition-colors cursor-pointer ${
                scrolled
                  ? "text-foreground-600 hover:bg-background-200"
                  : "text-white/80 hover:text-white hover:bg-white/10"
              }`}
              aria-label="菜单"
            >
              <i
                className={`text-lg ${
                  mobileOpen ? "ri-close-line" : "ri-menu-line"
                }`}
              ></i>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu panel */}
      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 px-4 py-4">
          <div className="flex flex-col gap-3">
            <Link
              to="/"
              className="text-sm text-foreground-700 hover:text-foreground-950 py-2"
            >
              首页
            </Link>
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="text-sm text-foreground-700 hover:text-foreground-950 py-2"
              >
                {cat.name}
              </Link>
            ))}
            <Link
              to="/about"
              className="text-sm text-foreground-700 hover:text-foreground-950 py-2"
            >
              关于我们
            </Link>
            <Link
              to="/"
              className="inline-flex justify-center px-4 py-2 rounded-full text-sm font-medium bg-primary-500 text-white hover:bg-primary-600 whitespace-nowrap transition-colors"
            >
              登录 / 注册
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}